import React, { useState, useEffect } from 'react'
import { useAuth } from '../../../context/AuthProvider'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Loader2, Upload, X, Check, Plus, Trash2, Clock, Users, Megaphone, Edit3, Eye, Calendar } from 'lucide-react'

export default function ParishProfile() {
  const { user, loading: authLoading } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [parishId, setParishId] = useState(null)
  const [parish, setParish] = useState(null)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)

  // Parish info state
  const [massTimes, setMassTimes] = useState([])
  const [confessionTimes, setConfessionTimes] = useState([])
  const [parishStaff, setParishStaff] = useState([])
  const [savingInfo, setSavingInfo] = useState(null)

  // Announcements state
  const [announcements, setAnnouncements] = useState([])
  const [announcementsLoading, setAnnouncementsLoading] = useState(false)
  const [announcementsMeta, setAnnouncementsMeta] = useState(null)
  const [announcementsPage, setAnnouncementsPage] = useState(1)
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState(null)
  const [savingAnnouncement, setSavingAnnouncement] = useState(false)
  const [deletingAnnouncementId, setDeletingAnnouncementId] = useState(null)
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    scheduleEnabled: false,
    publishedAt: '',
  })

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    email: '',
    phone: '',
    description: '',
  })

  // Fetch parish data - similar to donor page pattern
  useEffect(() => {
    if (user?.id) {
      fetchParishData()
    }
  }, [user?.id])

  const fetchParishData = async () => {
    try {
      setLoading(true)
      setError(null)

      if (!user?.id) {
        setError('User not found')
        setLoading(false)
        return
      }

      // Get user data which includes roleDetails
      const userData = await api.getMe()
      
      // Get parish ID from roleDetails - this is the parish associated with the parish-admin role
      const roleDetails = userData?.roleDetails || []
      const parishAdminRole = roleDetails.find(r => r.name === 'parish-admin')
      const id = parishAdminRole?.parishId
      
      if (!id) {
        setError('You are not associated with a parish as a parish admin. Please contact support.')
        setLoading(false)
        return
      }

      setParishId(id)

      // Fetch parish details
      const parishData = await api.getParish(id)
      
      setParish(parishData)
      setFormData({
        name: parishData.name || '',
        address: parishData.address || '',
        city: parishData.city || '',
        state: parishData.state || '',
        zipCode: parishData.zipCode || '',
        email: parishData.email || '',
        phone: parishData.phoneNumber || '',
        description: parishData.description || '',
      })
      setImagePreview(parishData.imageUrl || null)

      // Initialize parish info sections (migrate legacy flat format to grouped)
      const info = parishData.parishInfo || {}
      setMassTimes(migrateMassTimes(info.massTimes || []))
      setConfessionTimes(migrateConfessionTimes(info.confessionTimes || []))
      setParishStaff(info.parishStaff || [])

      // Fetch announcements after parish data is loaded
      fetchAnnouncements(1, id)
    } catch (err) {
      console.error('Failed to fetch parish data:', err)
      setError(err.message || 'Failed to load parish data')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
    // Clear success message when user starts editing
    if (success) setSuccess(null)
  }

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please select a JPEG, PNG, WebP, or GIF image.')
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size exceeds 5MB. Please select a smaller image.')
      return
    }

    setSelectedImage(file)
    setError(null)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
    setImagePreview(parish?.imageUrl || null)
  }

  const handleImageUpload = async () => {
    if (!selectedImage || !parishId) return

    try {
      setUploadingImage(true)
      setError(null)
      setSuccess(null)

      const updatedParish = await api.uploadParishImage(parishId, selectedImage)
      
      setParish(updatedParish)
      setImagePreview(updatedParish.imageUrl)
      setSelectedImage(null)
      setSuccess('Image uploaded successfully!')
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to upload image:', err)
      setError(err.message || 'Failed to upload image')
    } finally {
      setUploadingImage(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!parishId) {
      setError('Parish ID not found')
      return
    }

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Prepare update data (only include changed fields)
      if (!parish) {
        setError('Parish data not loaded')
        setSaving(false)
        return
      }

      const updateData = {}
      if (formData.name !== parish.name) updateData.name = formData.name
      if (formData.address !== (parish.address || '')) updateData.address = formData.address
      if (formData.city !== (parish.city || '')) updateData.city = formData.city
      if (formData.state !== (parish.state || '')) updateData.state = formData.state
      if (formData.zipCode !== (parish.zipCode || '')) updateData.zipCode = formData.zipCode
      if (formData.email !== (parish.email || '')) updateData.email = formData.email
      if (formData.phone !== (parish.phoneNumber || '')) updateData.phone = formData.phone
      if (formData.description !== (parish.description || '')) updateData.description = formData.description

      if (Object.keys(updateData).length === 0) {
        setSuccess('No changes to save')
        setTimeout(() => setSuccess(null), 3000)
        setSaving(false)
        return
      }

      const updatedParish = await api.updateParish(parishId, updateData)
      setParish(updatedParish)
      setSuccess('Parish information updated successfully!')
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to update parish:', err)
      setError(err.message || 'Failed to update parish information')
    } finally {
      setSaving(false)
    }
  }

  // Parish info save handlers
  const saveParishInfoSection = async (key, value, section) => {
    if (!parishId) return
    try {
      setSavingInfo(section)
      setError(null)
      setSuccess(null)

      await api.updateParish(parishId, { [key]: value })

      // Refresh parish data to get updated parishInfo
      const parishData = await api.getParish(parishId)
      setParish(parishData)
      const info = parishData.parishInfo || {}
      setMassTimes(info.massTimes || [])
      setConfessionTimes(info.confessionTimes || [])
      setParishStaff(info.parishStaff || [])

      setSuccess(`${section === 'mass' ? 'Mass times' : section === 'confession' ? 'Confession times' : 'Parish staff'} saved successfully!`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error(`Failed to save ${section}:`, err)
      setError(err.message || `Failed to save ${section}`)
    } finally {
      setSavingInfo(null)
    }
  }

  // Mass times helpers (grouped by day)
  const addMassTimeDay = () => setMassTimes(prev => [...prev, { day: 'Sunday', times: [{ time: '', note: '' }] }])
  const removeMassTimeDay = (dayIndex) => setMassTimes(prev => prev.filter((_, i) => i !== dayIndex))
  const updateMassTimeDay = (dayIndex, day) => {
    setMassTimes(prev => prev.map((item, i) => i === dayIndex ? { ...item, day } : item))
    if (success) setSuccess(null)
  }
  const addMassTimeSlot = (dayIndex) => {
    setMassTimes(prev => prev.map((item, i) => i === dayIndex ? { ...item, times: [...item.times, { time: '', note: '' }] } : item))
  }
  const removeMassTimeSlot = (dayIndex, slotIndex) => {
    setMassTimes(prev => prev.map((item, i) => {
      if (i !== dayIndex) return item
      const newTimes = item.times.filter((_, si) => si !== slotIndex)
      return { ...item, times: newTimes }
    }))
  }
  const updateMassTimeSlot = (dayIndex, slotIndex, field, value) => {
    setMassTimes(prev => prev.map((item, i) => {
      if (i !== dayIndex) return item
      const newTimes = item.times.map((slot, si) => si === slotIndex ? { ...slot, [field]: value } : slot)
      return { ...item, times: newTimes }
    }))
    if (success) setSuccess(null)
  }

  // Confession times helpers (grouped by day)
  const addConfessionTimeDay = () => setConfessionTimes(prev => [...prev, { day: 'Saturday', times: [{ startTime: '', endTime: '', note: '' }] }])
  const removeConfessionTimeDay = (dayIndex) => setConfessionTimes(prev => prev.filter((_, i) => i !== dayIndex))
  const updateConfessionTimeDay = (dayIndex, day) => {
    setConfessionTimes(prev => prev.map((item, i) => i === dayIndex ? { ...item, day } : item))
    if (success) setSuccess(null)
  }
  const addConfessionTimeSlot = (dayIndex) => {
    setConfessionTimes(prev => prev.map((item, i) => i === dayIndex ? { ...item, times: [...item.times, { startTime: '', endTime: '', note: '' }] } : item))
  }
  const removeConfessionTimeSlot = (dayIndex, slotIndex) => {
    setConfessionTimes(prev => prev.map((item, i) => {
      if (i !== dayIndex) return item
      const newTimes = item.times.filter((_, si) => si !== slotIndex)
      return { ...item, times: newTimes }
    }))
  }
  const updateConfessionTimeSlot = (dayIndex, slotIndex, field, value) => {
    setConfessionTimes(prev => prev.map((item, i) => {
      if (i !== dayIndex) return item
      const newTimes = item.times.map((slot, si) => si === slotIndex ? { ...slot, [field]: value } : slot)
      return { ...item, times: newTimes }
    }))
    if (success) setSuccess(null)
  }

  // Parish staff helpers
  const addStaffMember = () => setParishStaff(prev => [...prev, { title: '', name: '', email: '', phone: '' }])
  const removeStaffMember = (index) => setParishStaff(prev => prev.filter((_, i) => i !== index))
  const updateStaffMember = (index, field, value) => {
    setParishStaff(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item))
    if (success) setSuccess(null)
  }

  // Migrate legacy flat format [{ day, time }] to grouped format [{ day, times: [{ time, note }] }]
  const migrateMassTimes = (data) => {
    if (!Array.isArray(data) || data.length === 0) return data
    if (data[0].times) return data // already grouped
    const grouped = {}
    data.forEach(entry => {
      if (!grouped[entry.day]) grouped[entry.day] = []
      grouped[entry.day].push({ time: entry.time || '', note: '' })
    })
    return Object.entries(grouped).map(([day, times]) => ({ day, times }))
  }

  // Migrate legacy flat format [{ day, time }] to grouped format [{ day, times: [{ startTime, endTime, note }] }]
  const migrateConfessionTimes = (data) => {
    if (!Array.isArray(data) || data.length === 0) return data
    if (data[0].times) return data // already grouped
    const grouped = {}
    data.forEach(entry => {
      if (!grouped[entry.day]) grouped[entry.day] = []
      grouped[entry.day].push({ startTime: entry.time || '', endTime: '', note: '' })
    })
    return Object.entries(grouped).map(([day, times]) => ({ day, times }))
  }

  // Announcement functions
  const fetchAnnouncements = async (page = 1, id = parishId) => {
    if (!id) return
    try {
      setAnnouncementsLoading(true)
      const response = await api.getParishAnnouncements(id, { page, limit: 10 })
      setAnnouncements(response.data || [])
      setAnnouncementsMeta(response.meta || null)
      setAnnouncementsPage(page)
    } catch (err) {
      console.error('Failed to fetch announcements:', err)
    } finally {
      setAnnouncementsLoading(false)
    }
  }

  const resetAnnouncementForm = () => {
    setAnnouncementForm({ title: '', content: '', scheduleEnabled: false, publishedAt: '' })
    setEditingAnnouncement(null)
    setShowAnnouncementForm(false)
  }

  const handleNewAnnouncement = () => {
    resetAnnouncementForm()
    setShowAnnouncementForm(true)
  }

  const handleEditAnnouncement = (announcement) => {
    const scheduled = announcement.publishedAt && new Date(announcement.publishedAt) > new Date()
    setEditingAnnouncement(announcement)
    setAnnouncementForm({
      title: announcement.title,
      content: announcement.content,
      scheduleEnabled: !!scheduled,
      // datetime-local expects "YYYY-MM-DDTHH:MM" in local time
      publishedAt: scheduled ? toLocalDateTimeInput(announcement.publishedAt) : '',
    })
    setShowAnnouncementForm(true)
  }

  const handleAnnouncementFormChange = (e) => {
    const { name, value, type, checked } = e.target
    setAnnouncementForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
    if (success) setSuccess(null)
  }

  const handleSaveAnnouncement = async () => {
    if (!announcementForm.title.trim()) {
      setError('Announcement title is required')
      return
    }
    if (!announcementForm.content.trim()) {
      setError('Announcement content is required')
      return
    }

    try {
      setSavingAnnouncement(true)
      setError(null)
      setSuccess(null)

      const payload = {
        title: announcementForm.title.trim(),
        content: announcementForm.content.trim(),
      }

      if (announcementForm.scheduleEnabled) {
        const when = new Date(announcementForm.publishedAt)
        if (!announcementForm.publishedAt || isNaN(when.getTime())) {
          setError('Please choose a valid scheduled date and time')
          setSavingAnnouncement(false)
          return
        }
        payload.publishedAt = when.toISOString()
      } else if (
        editingAnnouncement &&
        editingAnnouncement.publishedAt &&
        new Date(editingAnnouncement.publishedAt) > new Date()
      ) {
        // Was scheduled, admin turned scheduling off -> publish now.
        payload.publishedAt = new Date().toISOString()
      }

      if (editingAnnouncement) {
        await api.updateAnnouncement(parishId, editingAnnouncement.id, payload)
        setSuccess('Announcement updated successfully!')
      } else {
        await api.createAnnouncement(parishId, payload)
        setSuccess('Announcement created successfully!')
      }

      resetAnnouncementForm()
      fetchAnnouncements(announcementsPage)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to save announcement:', err)
      setError(err.message || 'Failed to save announcement')
    } finally {
      setSavingAnnouncement(false)
    }
  }

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return

    try {
      setDeletingAnnouncementId(announcementId)
      setError(null)
      setSuccess(null)

      await api.deleteAnnouncement(parishId, announcementId)
      setSuccess('Announcement deleted successfully!')
      fetchAnnouncements(announcementsPage)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to delete announcement:', err)
      setError(err.message || 'Failed to delete announcement')
    } finally {
      setDeletingAnnouncementId(null)
    }
  }

  // Local "YYYY-MM-DDTHH:MM" string for <input type="datetime-local">
  const toLocalDateTimeInput = (iso) => {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

  // Show loading state - but don't block if we have auth but are just loading parish data
  if (authLoading) {
    return (
      <div>
        <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Parish Profile</h1>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      </div>
    )
  }

  if (loading && !parishId) {
    return (
      <div>
        <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Parish Profile</h1>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      </div>
    )
  }

  // Show error if no parish ID found
  if (error && !parishId) {
    return (
      <div>
        <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Parish Profile</h1>
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  // Show loading message if parish data not loaded yet
  if (!parish && parishId) {
    return (
      <div>
        <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Parish Profile</h1>
        <div className="bg-white border border-surface-border rounded p-6">
          <p className="text-charcoal-muted">Loading parish information...</p>
        </div>
      </div>
    )
  }

  // If no parish and no parishId, show error
  if (!parish && !parishId) {
    return (
      <div>
        <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Parish Profile</h1>
        <div className="bg-white border border-surface-border rounded p-6">
          <p className="text-charcoal-muted">Unable to load parish information. Please refresh the page.</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Parish Profile</h1>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded flex items-center gap-2">
          <Check className="w-5 h-5 text-green-400" />
          <p className="text-green-400 text-sm">{success}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Image Upload Section */}
      <div className="bg-white border border-surface-border rounded p-6">
          <h2 className="text-xl font-semibold font-display text-charcoal mb-4">Parish Image</h2>
          
          <div className="space-y-4">
            {/* Image Preview */}
            {imagePreview && (
              <div className="relative w-full max-w-md">
                <img
                  src={imagePreview}
                  alt="Parish preview"
                  className="w-full h-64 object-cover rounded border border-surface-border"
                />
                {selectedImage && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-700 rounded-full text-charcoal"
                    title="Remove selected image"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            {/* Image Upload Controls */}
            <div className="flex items-center gap-4">
              <Label htmlFor="image-upload" className="cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-2 bg-surface-linen border border-gold/30 rounded text-charcoal hover:bg-surface-border transition-colors">
                  <Upload className="w-4 h-4" />
                  <span>{selectedImage ? 'Change Image' : 'Upload Image'}</span>
                </div>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </Label>

              {selectedImage && (
                <Button
                  type="button"
                  onClick={handleImageUpload}
                  disabled={uploadingImage}
                  className="bg-gold text-black hover:bg-gold/90"
                >
                  {uploadingImage ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              )}
            </div>

            <p className="text-sm text-charcoal-muted">
              Upload a cover image for your parish (JPEG, PNG, WebP, or GIF, max 5MB)
            </p>
          </div>
        </div>

        {/* Parish Information Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-surface-border rounded p-6 space-y-6">
          <h2 className="text-xl font-semibold font-display text-charcoal mb-4">Parish Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Parish Name */}
            <div className="md:col-span-2">
              <Label htmlFor="name" className="text-charcoal">
                Parish Name *
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="mt-1 bg-surface-linen border-surface-border text-charcoal"
                placeholder="St. Mary's Catholic Church"
              />
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <Label htmlFor="address" className="text-charcoal">
                Street Address *
              </Label>
              <Input
                id="address"
                name="address"
                type="text"
                value={formData.address}
                onChange={handleInputChange}
                required
                className="mt-1 bg-surface-linen border-surface-border text-charcoal"
                placeholder="123 Main Street"
              />
            </div>

            {/* City */}
            <div>
              <Label htmlFor="city" className="text-charcoal">
                City *
              </Label>
              <Input
                id="city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleInputChange}
                required
                className="mt-1 bg-surface-linen border-surface-border text-charcoal"
                placeholder="New York"
              />
            </div>

            {/* State */}
            <div>
              <Label htmlFor="state" className="text-charcoal">
                State *
              </Label>
              <Input
                id="state"
                name="state"
                type="text"
                value={formData.state}
                onChange={handleInputChange}
                required
                maxLength={2}
                className="mt-1 bg-surface-linen border-surface-border text-charcoal uppercase"
                placeholder="NY"
                style={{ textTransform: 'uppercase' }}
              />
            </div>

            {/* ZIP Code */}
            <div>
              <Label htmlFor="zipCode" className="text-charcoal">
                ZIP Code *
              </Label>
              <Input
                id="zipCode"
                name="zipCode"
                type="text"
                value={formData.zipCode}
                onChange={handleInputChange}
                required
                pattern="^\d{5}(-\d{4})?$"
                className="mt-1 bg-surface-linen border-surface-border text-charcoal"
                placeholder="10001"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-charcoal">
                Email *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="mt-1 bg-surface-linen border-surface-border text-charcoal"
                placeholder="info@stmarys.org"
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="text-charcoal">
                Phone Number
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                className="mt-1 bg-surface-linen border-surface-border text-charcoal"
                placeholder="+1-555-123-4567"
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <Label htmlFor="description" className="text-charcoal">
                Description
              </Label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                maxLength={2000}
                className="mt-1 w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal placeholder-charcoal-faint focus:outline-none focus:ring-2 focus:ring-gold/50"
                placeholder="A welcoming Catholic community serving the downtown area since 1850."
              />
              <p className="mt-1 text-xs text-charcoal-muted">
                {formData.description.length}/2000 characters
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t border-surface-border">
            <Button
              type="submit"
              disabled={saving}
              className="bg-gold text-black hover:bg-gold/90"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>

        {/* Mass Times Section */}
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-gold" />
            <h2 className="text-xl font-semibold font-display text-charcoal">Mass Times</h2>
          </div>

          {massTimes.length === 0 ? (
            <p className="text-charcoal-muted text-sm mb-4">No mass times added yet.</p>
          ) : (
            <div className="space-y-4 mb-4">
              {massTimes.map((dayGroup, dayIndex) => (
                <div key={dayIndex} className="border border-surface-border rounded p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <select
                      value={dayGroup.day}
                      onChange={(e) => updateMassTimeDay(dayIndex, e.target.value)}
                      className="h-10 rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm font-semibold text-charcoal focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold w-44"
                    >
                      {DAYS_OF_WEEK.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeMassTimeDay(dayIndex)}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors"
                      title="Remove day"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2 pl-2">
                    {dayGroup.times.map((slot, slotIndex) => (
                      <div key={slotIndex} className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={slot.time}
                          onChange={(e) => updateMassTimeSlot(dayIndex, slotIndex, 'time', e.target.value)}
                          placeholder="e.g. 9:00 AM"
                          className="w-36 bg-surface-linen border-surface-border text-charcoal"
                        />
                        <Input
                          type="text"
                          value={slot.note || ''}
                          onChange={(e) => updateMassTimeSlot(dayIndex, slotIndex, 'note', e.target.value)}
                          placeholder="Note (optional)"
                          className="flex-1 bg-surface-linen border-surface-border text-charcoal"
                        />
                        <button
                          type="button"
                          onClick={() => removeMassTimeSlot(dayIndex, slotIndex)}
                          className="p-1.5 text-red-500 hover:text-red-700 transition-colors"
                          title="Remove time"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addMassTimeSlot(dayIndex)}
                      className="text-sm text-gold hover:text-gold/80 flex items-center gap-1 mt-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add time
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-surface-border">
            <Button type="button" variant="outline" onClick={addMassTimeDay}>
              <Plus className="w-4 h-4 mr-2" />
              Add Day
            </Button>
            <Button
              type="button"
              onClick={() => saveParishInfoSection('massTimes', massTimes, 'mass')}
              disabled={savingInfo === 'mass'}
              className="bg-gold text-black hover:bg-gold/90"
            >
              {savingInfo === 'mass' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Mass Times'
              )}
            </Button>
          </div>
        </div>

        {/* Confession Times Section */}
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-gold" />
            <h2 className="text-xl font-semibold font-display text-charcoal">Confession Times</h2>
          </div>

          {confessionTimes.length === 0 ? (
            <p className="text-charcoal-muted text-sm mb-4">No confession times added yet.</p>
          ) : (
            <div className="space-y-4 mb-4">
              {confessionTimes.map((dayGroup, dayIndex) => (
                <div key={dayIndex} className="border border-surface-border rounded p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <select
                      value={dayGroup.day}
                      onChange={(e) => updateConfessionTimeDay(dayIndex, e.target.value)}
                      className="h-10 rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm font-semibold text-charcoal focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold w-44"
                    >
                      {DAYS_OF_WEEK.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeConfessionTimeDay(dayIndex)}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors"
                      title="Remove day"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2 pl-2">
                    {dayGroup.times.map((slot, slotIndex) => (
                      <div key={slotIndex} className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={slot.startTime}
                          onChange={(e) => updateConfessionTimeSlot(dayIndex, slotIndex, 'startTime', e.target.value)}
                          placeholder="Start (e.g. 3:00 PM)"
                          className="w-40 bg-surface-linen border-surface-border text-charcoal"
                        />
                        <span className="text-charcoal-muted text-sm">to</span>
                        <Input
                          type="text"
                          value={slot.endTime}
                          onChange={(e) => updateConfessionTimeSlot(dayIndex, slotIndex, 'endTime', e.target.value)}
                          placeholder="End (e.g. 4:00 PM)"
                          className="w-40 bg-surface-linen border-surface-border text-charcoal"
                        />
                        <Input
                          type="text"
                          value={slot.note || ''}
                          onChange={(e) => updateConfessionTimeSlot(dayIndex, slotIndex, 'note', e.target.value)}
                          placeholder="Note (optional)"
                          className="flex-1 bg-surface-linen border-surface-border text-charcoal"
                        />
                        <button
                          type="button"
                          onClick={() => removeConfessionTimeSlot(dayIndex, slotIndex)}
                          className="p-1.5 text-red-500 hover:text-red-700 transition-colors"
                          title="Remove time"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addConfessionTimeSlot(dayIndex)}
                      className="text-sm text-gold hover:text-gold/80 flex items-center gap-1 mt-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add time slot
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-surface-border">
            <Button type="button" variant="outline" onClick={addConfessionTimeDay}>
              <Plus className="w-4 h-4 mr-2" />
              Add Day
            </Button>
            <Button
              type="button"
              onClick={() => saveParishInfoSection('confessionTimes', confessionTimes, 'confession')}
              disabled={savingInfo === 'confession'}
              className="bg-gold text-black hover:bg-gold/90"
            >
              {savingInfo === 'confession' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Confession Times'
              )}
            </Button>
          </div>
        </div>

        {/* Parish Staff Section */}
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-gold" />
            <h2 className="text-xl font-semibold font-display text-charcoal">Parish Staff</h2>
          </div>

          {parishStaff.length === 0 ? (
            <p className="text-charcoal-muted text-sm mb-4">No staff members added yet.</p>
          ) : (
            <div className="space-y-4 mb-4">
              {parishStaff.map((member, index) => (
                <div key={index} className="border border-surface-border rounded p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-charcoal-muted">Staff Member {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeStaffMember(index)}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-charcoal text-xs">Title *</Label>
                      <Input
                        type="text"
                        value={member.title}
                        onChange={(e) => updateStaffMember(index, 'title', e.target.value)}
                        placeholder="e.g. Pastor"
                        className="mt-1 bg-surface-linen border-surface-border text-charcoal"
                      />
                    </div>
                    <div>
                      <Label className="text-charcoal text-xs">Name *</Label>
                      <Input
                        type="text"
                        value={member.name}
                        onChange={(e) => updateStaffMember(index, 'name', e.target.value)}
                        placeholder="e.g. Fr. John Smith"
                        className="mt-1 bg-surface-linen border-surface-border text-charcoal"
                      />
                    </div>
                    <div>
                      <Label className="text-charcoal text-xs">Email</Label>
                      <Input
                        type="email"
                        value={member.email || ''}
                        onChange={(e) => updateStaffMember(index, 'email', e.target.value)}
                        placeholder="Optional"
                        className="mt-1 bg-surface-linen border-surface-border text-charcoal"
                      />
                    </div>
                    <div>
                      <Label className="text-charcoal text-xs">Phone</Label>
                      <Input
                        type="tel"
                        value={member.phone || ''}
                        onChange={(e) => updateStaffMember(index, 'phone', e.target.value)}
                        placeholder="Optional"
                        className="mt-1 bg-surface-linen border-surface-border text-charcoal"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-surface-border">
            <Button type="button" variant="outline" onClick={addStaffMember}>
              <Plus className="w-4 h-4 mr-2" />
              Add Staff Member
            </Button>
            <Button
              type="button"
              onClick={() => saveParishInfoSection('parishStaff', parishStaff, 'staff')}
              disabled={savingInfo === 'staff'}
              className="bg-gold text-black hover:bg-gold/90"
            >
              {savingInfo === 'staff' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Staff'
              )}
            </Button>
          </div>
        </div>

        {/* Announcements Section */}
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-gold" />
              <h2 className="text-xl font-semibold font-display text-charcoal">Announcements</h2>
            </div>
            {!showAnnouncementForm && (
              <Button type="button" variant="outline" onClick={handleNewAnnouncement}>
                <Plus className="w-4 h-4 mr-2" />
                New Announcement
              </Button>
            )}
          </div>

          {/* Create/Edit Form */}
          {showAnnouncementForm && (
            <div className="border border-gold/30 rounded p-4 mb-4 space-y-4 bg-surface-linen">
              <h3 className="text-base font-semibold text-charcoal">
                {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
              </h3>
              <div>
                <Label htmlFor="ann-title" className="text-charcoal text-xs">Title *</Label>
                <Input
                  id="ann-title"
                  name="title"
                  type="text"
                  value={announcementForm.title}
                  onChange={handleAnnouncementFormChange}
                  placeholder="e.g. Christmas Mass Schedule"
                  maxLength={200}
                  className="mt-1 bg-white border-surface-border text-charcoal"
                />
                <p className="mt-1 text-xs text-charcoal-muted">{announcementForm.title.length}/200 characters</p>
              </div>
              <div>
                <Label htmlFor="ann-content" className="text-charcoal text-xs">Content *</Label>
                <textarea
                  id="ann-content"
                  name="content"
                  value={announcementForm.content}
                  onChange={handleAnnouncementFormChange}
                  rows={4}
                  className="mt-1 w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal placeholder-charcoal-faint focus:outline-none focus:ring-2 focus:ring-gold/50"
                  placeholder="Write your announcement content here..."
                />
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="scheduleEnabled"
                    checked={announcementForm.scheduleEnabled}
                    onChange={handleAnnouncementFormChange}
                    className="w-4 h-4 text-gold border-surface-border rounded focus:ring-gold"
                  />
                  <span className="text-sm text-charcoal font-medium">Schedule for later</span>
                </label>
                {announcementForm.scheduleEnabled ? (
                  <div>
                    <Label htmlFor="ann-publish-at" className="text-charcoal text-xs">
                      Scheduled Time
                    </Label>
                    <Input
                      id="ann-publish-at"
                      name="publishedAt"
                      type="datetime-local"
                      value={announcementForm.publishedAt}
                      onChange={handleAnnouncementFormChange}
                      className="mt-1 bg-white border-surface-border text-charcoal"
                    />
                    <p className="mt-1 text-xs text-charcoal-muted">The announcement will appear automatically at this time.</p>
                  </div>
                ) : (
                  <p className="text-xs text-charcoal-muted">Posts immediately and stays until you delete it.</p>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-surface-border">
                <Button type="button" variant="ghost" onClick={resetAnnouncementForm}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveAnnouncement}
                  disabled={savingAnnouncement}
                  className="bg-gold text-black hover:bg-gold/90"
                >
                  {savingAnnouncement ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : editingAnnouncement ? (
                    'Update Announcement'
                  ) : (
                    'Create Announcement'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Announcements List */}
          {announcementsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-gold animate-spin" />
            </div>
          ) : announcements.length === 0 ? (
            <p className="text-charcoal-muted text-sm">No announcements yet. Create your first announcement to communicate with your parishioners.</p>
          ) : (
            <div className="space-y-3">
              {announcements.map((announcement) => (
                <div key={announcement.id} className="border border-surface-border rounded p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="text-base font-semibold text-charcoal truncate">{announcement.title}</h4>
                        {announcement.publishedAt && new Date(announcement.publishedAt) > new Date() ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-gold/15 text-charcoal">
                            <Calendar className="w-3 h-3" />
                            Scheduled {new Date(announcement.publishedAt).toLocaleString()}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-green-50 text-green-700">
                            <Eye className="w-3 h-3" />
                            Posted
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-charcoal-muted line-clamp-2 whitespace-pre-wrap">{announcement.content}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-charcoal-muted">
                        {announcement.author && (
                          <span>By {announcement.author.name || announcement.author.email}</span>
                        )}
                        <span>Created {new Date(announcement.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleEditAnnouncement(announcement)}
                        className="p-1.5 text-charcoal-muted hover:text-charcoal transition-colors"
                        title="Edit"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteAnnouncement(announcement.id)}
                        disabled={deletingAnnouncementId === announcement.id}
                        className="p-1.5 text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        {deletingAnnouncementId === announcement.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {announcementsMeta && announcementsMeta.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-surface-border">
              <div className="text-sm text-charcoal-muted">
                Page {announcementsMeta.page} of {announcementsMeta.totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fetchAnnouncements(announcementsPage - 1)}
                  disabled={!announcementsMeta.hasPrevPage || announcementsLoading}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fetchAnnouncements(announcementsPage + 1)}
                  disabled={!announcementsMeta.hasNextPage || announcementsLoading}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
